import { Link } from "react-router";

import { CalendarIcon, ClockIcon, MailIcon, SettingsIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { AccountOverview } from "@/features/account/types";
import { User } from "@/shared/lib/types/user";
import { formatAbsoluteDateTime, formatRelativeDate } from "@/shared/lib/utils/date";
import { getFullName, getInitials } from "@/shared/lib/utils/user";

interface ProfileIdentityCardProps {
  user: User;
  overview?: AccountOverview;
}

export function ProfileIdentityCard({ user, overview }: ProfileIdentityCardProps) {
  const fullName = getFullName(user.firstName, user.lastName);

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card">
      <CardContent className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-5">
          <Avatar size="lg" className="size-16">
            <AvatarImage src="" alt={fullName} />
            <AvatarFallback className="text-lg font-semibold">{getInitials(user.firstName, user.lastName)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-heading text-2xl font-semibold">{fullName}</h1>
              {overview && (
                <Badge variant="secondary" className="text-xs">
                  {overview.stats.projectCount} {overview.stats.projectCount === 1 ? "project" : "projects"}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MailIcon className="size-3.5 shrink-0" />
              {user.emailAddress}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
              {overview ? (
                <>
                  <Tooltip delayDuration={400}>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1.5">
                        <CalendarIcon className="size-3.5 shrink-0" />
                        Member since {overview.createdAt.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{formatAbsoluteDateTime(overview.createdAt)}</TooltipContent>
                  </Tooltip>
                  <Tooltip delayDuration={400}>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1.5">
                        <ClockIcon className="size-3.5 shrink-0" />
                        Last active {formatRelativeDate(overview.lastActiveAt)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{formatAbsoluteDateTime(overview.lastActiveAt)}</TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="h-3 w-28" />
                </>
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/settings">
            <SettingsIcon />
            Edit profile
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

export function ProfileIdentityCardSkeleton() {
  return (
    <Card>
      <CardContent className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-5">
          <Skeleton className="size-16 rounded-full" />
          <div className="flex flex-col gap-2.5">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-56" />
            <Skeleton className="h-3 w-64" />
          </div>
        </div>
        <Skeleton className="h-8 w-28" />
      </CardContent>
    </Card>
  );
}
